import React, { useRef, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import _ from 'underscore';
import { Button, Card, CardHeader, CardContent, CardActions, Grid, Divider, colors } from '@mui/material';
import { Page, Header, SelectField, FilePicker } from 'components';
import validate from 'validate.js';

import { enqueueSnackbar } from 'actions';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { SetFormState, GenerateTemplate } from '../../../actions/TechAddActions.js';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const schema = {
	selectedFile: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	selectedTemplates: {
		presence: { allowEmpty: false, message: 'is required' },
	},
};

const TechAddForm = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.techAddReducer.formState.techAdd);
	const templates = useSelector((state) => state.techAddReducer.templates);
	const user = useSelector((state) => state.session.user);

	useEffect(() => {
		const errors = validate(formState.values, schema);
		setFormState({
			techAdd: {
				...formState,
				isValid: !errors,
				errors: errors || {},
			}
		});
	}, [formState.values]);

	const _handleChange = (event, value) => {
		setFormState({
			techAdd: {
				...formState,
				values: {
					...formState.values,
					[event.target.name]:
						event.target.type === 'checkbox'
							? event.target.checked
							: event.target.value,
					selectedTemplates: value.map(u => u.value).join('+')
				},
				touched: {
					...formState.touched,
					[event.target.name]: true,
				},
			}
		});
	};

	// const hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));

	const _onChangeInput = (event) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			setFormState({
				techAdd: {
					...formState,
					values: {
						...formState.values,
						selectedFile: file,
					},
					touched: {
						...formState.touched,
						selectedFile: true,
					},
				}
			});
		}
	};

	const _generate = () => {
		if (formState.values.selectedFile.size > 100000000) {
			dispatch(enqueueSnackbar({ message: 'File size cannot be superior to 100MB', options: { variant: 'warning' } }));
			return;
		}
		dispatch(GenerateTemplate(formState.values.selectedFile, user.id, formState.values.selectedTemplates));
	};

	return (
		<Card>
			<CardHeader title="Tech Add" />
			<Divider />
			<CardContent>
				<Grid container spacing={1}>
					<Grid item md={4} xs={12}>
						<FilePicker value={formState.values.selectedFile?.name} onChange={_onChangeInput} accept=".zip" />
					</Grid>
					<Grid item md={6} xs={12}>
						<Autocomplete
							multiple
							options={templates}
							id="selectedTemplates"
							name="selectedTemplates"
							groupBy={(option) => (option.value.slice(0, 3) === 'L8_' ? option.value.slice(0, 2) : option.value.slice(0, 3))}
							disableCloseOnSelect
							onChange={_handleChange}
							getOptionLabel={(option) => option.value}
							renderOption={(props, option, { selected }) => (
								<li {...props}>
									<Checkbox
										icon={icon}
										checkedIcon={checkedIcon}
										style={{ marginRight: 8 }}
										checked={(selected || _.findWhere(formState.values.selectedTemplates, option) !== undefined)}
									/>
									{option.value}
								</li>
							)}
							// style={{ width: 750 }}
							renderInput={(params) => (<TextField {...params} label="Select Template(s)" placeholder="" />)}
						/>
					</Grid>
				</Grid>
			</CardContent>
			<Divider />
			<CardActions>
				<Button className={classes.generate} onClick={_generate} variant="contained" disabled={!formState.isValid}>Generate</Button>
			</CardActions>
		</Card>
	);
};

export default TechAddForm;
